import apisauce, {
  type ApiResponse,
  type ApisauceInstance,
  CancelToken
} from "apisauce"
import { type Axios } from "axios"

import { createFormDataNested } from "../utils/form-data/create-form-data-nested"
import { handleGetResponseData } from "../utils/handlers/handle-get-response-data"
import {
  CLIENT_HEADER,
  WEB_CLIENT_ID,
  needsClientHeader
} from "./client-header"
import type {
  APIInstance,
  AccessData,
  AuthResponse,
  AuthResult,
  CreateAccountData,
  EmailConfirmResponse,
  SignInData
} from "./types"
import {
  DEFAULT_REQUEST_TIMEOUT_MS,
  applyUploadTimeout
} from "./upload-timeout"

// Utility Functions
export const encryptedData = <T>(data: T): T => {
  // Encryption logic can be implemented here if needed
  // let encrypted = generateToken().encode(JSON.stringify(data))
  // return ({encrypted})
  return data
}

const serialize = (obj: Record<string, unknown>, prefix?: string): string => {
  const str: string[] = []

  for (const p in obj) {
    if (Object.hasOwn(obj, p)) {
      const k = prefix || p
      const v = obj[p]
      str.push(
        v !== null && typeof v === "object"
          ? serialize(v as Record<string, unknown>, k)
          : `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
      )
    }
  }
  return str.join("&")
}

export class Api {
  private static instance: Api
  private baseURL: string
  public api!: ApisauceInstance
  public source!: () => void
  public interceptors!: Axios["interceptors"] // Add interceptors property

  constructor() {
    this.baseURL = import.meta.env.VITE_APP_BASE_API_ROUTE

    if (!Api.instance) {
      this.create()
      this.createInterceptor()
      Api.instance = this
    }

    return Api.instance
  }

  private create(): void {
    this.api = apisauce.create({
      baseURL: this.baseURL,
      headers: {
        "Content-Type": "application/json"
      },
      cancelToken: new CancelToken((cancel) => {
        this.source = cancel
      }),
      timeout: DEFAULT_REQUEST_TIMEOUT_MS
    })
  }

  public cancelAllRequest(): void {
    Api.instance.source()
  }

  private createInterceptor(): void {
    const axiosInstance = this.api.axiosInstance

    // Store interceptors reference like in JS version
    this.interceptors = axiosInstance.interceptors

    // Request interceptor
    axiosInstance.interceptors.request.use(
      (config: any) => {
        if (needsClientHeader(config.url)) {
          config.headers[CLIENT_HEADER] = WEB_CLIENT_ID
        }
        return applyUploadTimeout(config)
      },
      (error: any) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor
    axiosInstance.interceptors.response.use(
      (response) => {
        // Add any response modifications here
        return response
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }
}

export const request = new Api()

// API Factory Function
const create = (
  baseURL: string = import.meta.env.VITE_APP_BASE_API_ROUTE
): APIInstance => {
  const accessData: AccessData = { accessToken: null, refreshToken: null }

  const api = apisauce.create({
    baseURL,
    headers: {
      "Content-Type": "application/json"
    },
    timeout: DEFAULT_REQUEST_TIMEOUT_MS
  })

  api.axiosInstance.interceptors.request.use((config) => {
    if (needsClientHeader(config.url)) {
      config.headers.set(CLIENT_HEADER, WEB_CLIENT_ID)
    }
    return config
  })

  // Token Management
  const updateAccessToken = (
    accessToken: string | null,
    refreshToken: string | null = null
  ): void => {
    accessData.accessToken = accessToken
    accessData.refreshToken = refreshToken

    if (accessToken) {
      api.setHeader("Authorization", `JWT ${accessToken}`)
    } else {
      delete api.headers["Authorization"]
    }
  }

  // Helper for handling auth responses
  const handleAuthResponse = <T extends AuthResponse | EmailConfirmResponse>(
    responseData: ApiResponse<T>
  ): Promise<AuthResult | ApiResponse<unknown>> => {
    return new Promise((resolve, reject) => {
      try {
        const response = handleGetResponseData(responseData)

        if (response?.ok && response.data?.access) {
          updateAccessToken(response.data.access, response.data.refresh)
          resolve({
            userInfo: response.data.user,
            access: response.data.access,
            refresh: response.data.refresh
          })
        } else if (!response.ok && response.data) {
          resolve(response)
        } else {
          reject(response)
        }
      } catch (error) {
        reject(error)
      }
    })
  }

  // Authentication Methods
  const signIn = async (
    data: SignInData
  ): Promise<AuthResult | ApiResponse<unknown>> => {
    const responseData = await api.post<AuthResponse>(
      "auth/token/",
      encryptedData(data)
    )
    return handleAuthResponse(responseData)
  }

  const logout = (tokens?: {
    access?: string | null
    refresh?: string | null
  }): Promise<ApiResponse<unknown>> => {
    const access = tokens?.access ?? accessData.accessToken
    const refresh = tokens?.refresh ?? accessData.refreshToken
    return api.post("auth/token/logout/", encryptedData({ refresh }), {
      headers: { Authorization: `JWT ${access}` }
    })
  }

  // Email Verification
  const resendVerificationLink = (
    data: Record<string, unknown>
  ): Promise<ApiResponse<unknown>> => {
    return api.post(`auth/resend-verification-code/`, encryptedData(data))
  }

  const confirmEmail = async (
    hash: string
  ): Promise<AuthResult | ApiResponse<unknown>> => {
    const query = serialize({ code: hash, auth: true })
    const responseData = await api.get<EmailConfirmResponse>(
      `auth/confirm-email/?${query}`
    )
    return handleAuthResponse(responseData)
  }

  // Account Management
  const createAccount = ({
    avatar,
    ...data
  }: CreateAccountData): Promise<ApiResponse<unknown>> => {
    // Documents uploaded on an invitation (W-9, insurance, license) need
    // multipart just as the avatar does; a plain invitation stays JSON.
    const hasDocument = Object.values(data).some((value) => value instanceof File)

    if (!avatar && !hasDocument) {
      return api.post(`auth/accept-invite/`, encryptedData(data))
    }

    const headers = { "Content-Type": "multipart/form-data" }
    const formData = createFormDataNested(data)
    if (avatar) formData.append("avatar", avatar)

    return request.api.post("auth/accept-invite/", formData, { headers })
  }

  // Password Management
  const checkPassword = (email: string): Promise<ApiResponse<unknown>> => {
    return api.post(`auth/forgot-password/`, encryptedData({ email }))
  }

  const confirmPassword = async (
    code: string
  ): Promise<AuthResult | ApiResponse<unknown>> => {
    const query = serialize({ code })
    const responseData = await api.get<EmailConfirmResponse>(
      `auth/forgot-password/?${query}`
    )
    return handleAuthResponse(responseData)
  }

  const createUserPassword = (
    new_password: string
  ): Promise<ApiResponse<unknown>> => {
    return api.post(
      `/user/account/set_password/`,
      encryptedData({ new_password })
    )
  }

  return {
    updateAccessToken,
    signIn,
    logout,
    resendVerificationLink,
    createAccount,
    confirmEmail,
    checkPassword,
    confirmPassword,
    createUserPassword
  }
}

export default create()
